<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import TabPanel from "@/components/ui/TabPanel.vue";
import TabPanelContent from "@/components/ui/TabPanelContent.vue";
import UserAvatar from "@/components/ui/UserAvatar.vue";
import { getReportName, getUserFriendlyName, type EstimatorReportDto } from "@/dto";
import { useApi } from "@/services/api";
import { useClipboard } from "@/services/clipboard";
import EstimatorReportViewFeatures from "@/views/reports/EstimatorReportViewFeatures.vue";
import EstimatorReportViewModel from "@/views/reports/EstimatorReportViewModel.vue";
import EstimatorReportViewPerformance from "@/views/reports/EstimatorReportViewPerformance.vue";
import { format } from "date-fns/format";

const { report, useAnchor = false } = defineProps<{
  report: EstimatorReportDto;
  useAnchor?: boolean;
}>();

const api = useApi();
const clipboard = useClipboard();
const tabContentHeight = ref(0);
const creatorName = ref("");

const reportName = computed(() => getReportName(report));

function formatDatetime(dt: string) {
  return format(new Date(dt), "d MMMM yyyy hh:mm");
}

function computeRemainingHeight() {
  const tabs = document.querySelector(".tabs") as HTMLDivElement;
  if (tabs) {
    tabContentHeight.value = window.innerHeight - tabs.offsetTop;
  }
}
async function fetchUser() {
  const user = await api.fetchUser(report.creator_id);
  if (user) {
    creatorName.value = getUserFriendlyName(user);
  }
}

async function copyReportId() {
  await clipboard.copyText(report.id.toString());
}

const resizeObserver = new ResizeObserver(() => {
  computeRemainingHeight();
});

onMounted(async () => {
  await fetchUser();
  computeRemainingHeight();
  resizeObserver.observe(document.body);
});

onBeforeUnmount(() => {
  resizeObserver.unobserve(document.body);
});
</script>

<template>
  <div class="estimator-report">
    <header>
      <div class="title">
        <slot name="preTitle"></slot>
        <h6><i class="icon icon-test-tube" />{{ reportName }}</h6>
      </div>
      <div class="info">
        <div>
          <div class="name">Created at:</div>
          <div class="value">{{ formatDatetime(report.created_at) }}</div>
        </div>
        <div>
          <div class="name">Creator:</div>
          <div class="value">
            <UserAvatar :name="creatorName" />
          </div>
        </div>
        <div class="copy-id" @click="copyReportId">
          <div class="name">Report ID:</div>
          <div class="value">{{ report.id }} <i class="icon icon-copy" /></div>
        </div>
      </div>
    </header>
    <TabPanel
      v-if="report"
      :uses-anchor="useAnchor"
      :style="{ '--content-height': `${tabContentHeight}px` }"
      @vue:mounted="computeRemainingHeight"
    >
      <TabPanelContent name="Performance">
        <EstimatorReportViewPerformance :report="report" />
      </TabPanelContent>
      <TabPanelContent name="Feature Importance">
        <EstimatorReportViewFeatures :report="report" />
      </TabPanelContent>
      <TabPanelContent name="Model">
        <EstimatorReportViewModel :report="report" />
      </TabPanelContent>
    </TabPanel>
  </div>
</template>

<style scoped>
.estimator-report {
  header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .title {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-6);

      h6 {
        .icon {
          margin-right: var(--spacing-6);
          color: var(--color-icon-tertiary);
          vertical-align: middle;
        }
      }
    }

    .info {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-8);

      & > div {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-12);

        .name {
          color: var(--color-text-secondary);
          font-weight: var(--font-weight-medium);
        }

        &.copy-id {
          cursor: pointer;

          i {
            padding: var(--spacing-4);
            border: solid var(--color-stroke-background-primary) var(--stroke-width-m);
            border-radius: var(--radius-xs);
            vertical-align: middle;
          }
        }
      }
    }
  }

  :deep(.tab-selector) {
    margin-bottom: var(--spacing-24);
  }

  :deep(.tab-content) {
    height: calc(var(--content-height) - var(--spacing-20));
  }

  :deep(.tabs) {
    .block-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: var(--spacing-16);

      & > div {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: var(--spacing-4);
      }

      .title-icon {
        display: inline-flex;
        width: 32px;
        height: 32px;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-s);
        margin-right: var(--spacing-8);
        background-color: var(--color-button-background-tertiary-hover);
      }
    }
  }
}
</style>
